import { cn } from '@mlm/ui';
import type { PosPriceMap, PosProduct } from '../../lib/api';
import { productPrice, variantPrice } from '../../lib/pos-pricing';
import { PriceTag } from './price-tag';
import { ProductImage } from './product-image';

/** What the tile says about availability, under the name. */
function stockLabel(p: PosProduct): string {
  // A course product is ALWAYS virtual (the API refuses to link a non-virtual
  // product to a course), so the two labels are mutually exclusive — "Course"
  // is the more specific of the pair and wins.
  if (p.isCourse) return 'Course';
  if (p.isVirtual) return 'Virtual';
  if (p.variants.length > 0) return `${p.variants.length} variations`;
  // Offline, the count may be out of date and never stops a sale, so say so.
  if (p.offlineStock != null) return `Last count: ${Math.max(p.offlineStock, 0)}`;
  if (p.assembledFromComponents) return `${p.sellableQty} available (assembled)`;
  // A stockist's extra units come from opening a bundle it holds, not from its
  // shelf — say so, or it reads as stock admin says isn't there.
  if (p.sellableQty > p.qty) return `${p.qty} on hand · ${p.sellableQty - p.qty} if opened`;
  return `${p.sellableQty} in stock`;
}

/**
 * One product on the till grid: photo, name, price, stock. A sold-out product
 * stays on the grid under a "No Inventory" veil, so the cashier can tell the
 * customer it exists but is out, rather than hunting for a tile that vanished.
 */
export function ProductTile({
  product: p,
  priceMap,
  onTap,
  compact = false,
}: {
  product: PosProduct;
  priceMap: PosPriceMap | null;
  onTap: (p: PosProduct) => void;
  /**
   * No photo block. For a catalog that has no photos at all, where a grid of
   * initials would only cost the cashier four times the scrolling.
   */
  compact?: boolean;
}) {
  const soldOut = p.sellableQty < 1;
  const low = !p.isVirtual && !soldOut && p.sellableQty < 5;
  return (
    <button
      type="button"
      onClick={() => onTap(p)}
      disabled={soldOut}
      className={cn(
        'flex flex-col rounded-[5px] bg-card p-[5px] text-left transition-transform active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100',
        compact && 'min-h-[92px] disabled:opacity-50',
      )}
    >
      {!compact && (
        <div className="relative">
          <ProductImage product={p} className="aspect-square w-full rounded-[5px]" />
          {soldOut && (
            <div className="absolute inset-0 flex items-center justify-center rounded-[5px] bg-black/50 px-2 text-center text-sm font-semibold text-white">
              No Inventory
            </div>
          )}
        </div>
      )}
      <div className={cn('flex flex-1 flex-col px-1 pb-1', compact ? 'pt-1' : 'pt-2')}>
        <div className="line-clamp-2 text-base capitalize leading-tight">{p.name}</div>
        <div className="mt-auto pt-1.5 text-sm">
          {p.variants.length > 0 ? (
            <PriceTag
              prefix="from "
              base={Math.min(...p.variants.map((v) => variantPrice(v, priceMap).base))}
              now={Math.min(...p.variants.map((v) => variantPrice(v, priceMap).now))}
            />
          ) : (
            <PriceTag base={productPrice(p, priceMap).base} now={productPrice(p, priceMap).now} />
          )}
        </div>
        <div
          className={cn(
            'text-xs',
            soldOut ? 'text-destructive' : low ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          {stockLabel(p)}
        </div>
      </div>
    </button>
  );
}
